import React, { useCallback, useRef } from 'react';
import { Spin } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';

import {
  hasMoreSelector,
  notificationsSelector,
  lastNotificationIdSelector,
} from 'redux/selectors/notificationSelectors';
import img from 'assets/img/notifyEmpty.svg';
import NotificationCard from './NotificationCard';
import { DirectionEnum } from 'api/backofficeNotificationApiClient';
import { getNotificationsList } from 'redux/reducers/notificationSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';

import styles from '../HeaderNotifications.module.less';

type Props = {
  onNotificationClick: () => void;
};

export default React.memo((props: Props) => {
  const { onNotificationClick } = props;
  const infiniteScrollParentRef = useRef(null);
  const dispatch = useAppDispatch();
  const hasMore = useAppSelector(hasMoreSelector);
  const notifications = useAppSelector(notificationsSelector);
  const lastNotificationId = useAppSelector(lastNotificationIdSelector);
  const isAdmin = useAppSelector(isAdminSelector);

  const onLoadMore = useCallback(
    (page: number) => {
      if (!lastNotificationId) return;

      dispatch(
        getNotificationsList({
          notificationId: lastNotificationId as number,
          direction: DirectionEnum.Down,
          isUserAdmin: isAdmin,
        }),
      );
    },
    [lastNotificationId, dispatch, isAdmin],
  );

  return (
    <div className={styles.notifyList} ref={infiniteScrollParentRef}>
      {!Boolean(notifications.length) && (
        <div className={styles.notifyEmpty}>
          <div className={styles.notifyEmptyImg}>
            <img src={img} alt="No notifications" />
          </div>
          <div className={styles.notifyEmptyTitle}>No new notifications.</div>
          <div className={styles.notifyEmptyText}>
            Great job! As you get going, new notifications will appear here
            instantly.
          </div>
        </div>
      )}

      <InfiniteScroll
        pageStart={0}
        loadMore={onLoadMore}
        hasMore={hasMore}
        loader={<Spin key={0} />}
        useWindow={false}
        getScrollParent={() => infiniteScrollParentRef.current}
      >
        {notifications.map((notification) => (
          <NotificationCard
            key={notification.notificationId}
            notification={notification}
            onNotificationClick={onNotificationClick}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
});
